import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { StatePicker } from "components/StatePicker";
import { CommunicationType } from "generated-graphql/graphql";
import { Controller } from "react-hook-form";
import { RecipientType } from "../../utils";
import { useCommunicationFormContext } from "../hooks/useCommunicationForm";
import { useRecipientForm } from "../hooks/useRecipientForm";
import { EmailArrayField } from "./EmailArrayField";
import { EmailBodyEditor } from "./EmailEditor/EmailBodyEditor";
import { RecipientPicker } from "./RecipientPicker";

interface RecipientCardProps {
  index: number;
  onRemove: () => void;
}

const CompactFormControlLabel = styled(FormControlLabel)({
  marginLeft: -8,
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
  },
  "& .MuiRadio-root": {
    padding: "4px 8px",
  },
});

const CompactRadioGroup = styled(RadioGroup)({
  "& .MuiFormControlLabel-root": {
    marginBottom: 2,
  },
});

export const RecipientCard = ({ index, onRemove }: RecipientCardProps) => {
  const { watch, mode } = useCommunicationFormContext();
  const viewDisabled = mode === "view";
  const tenant = watch("tenant");
  const facilities = watch("facilities");
  const facilityIds = facilities.map((facility) => facility.id);
  const disabled = viewDisabled || !tenant;
  const {
    formContext,
    recipientType,
    isEmailAllowed,
    isMailAllowed,
    preferUncertified,
    onChooseCommunicationType,
    onChangeRecipientType,
  } = useRecipientForm(index);
  const { control } = formContext;
  const watchedRecipient = watch(`recipients.${index}`);
  const rowId = watchedRecipient.rowId;
  const communicationType = watchedRecipient.communicationType;

  return (
    <Box sx={{ mr: 0.5, ml: 0.5 }}>
      <Grid container spacing={2}>
        <Grid item xs={3.5} md={1.75}>
          <RadioGroupField
            label="Recipient Type"
            required
            name={`recipients.${index}.recipientType`}
            control={control}
            fullWidth={false}
            disabled={disabled}
            direction="column"
            onChange={(e) => {
              onChangeRecipientType();
            }}
            radioOptions={[
              {
                label: "LEPC",
                value: RecipientType.LEPC,
              },
              {
                label: "FD",
                value: RecipientType.FIRE_DEPARTMENT,
              },
              {
                label: "PD",
                value: RecipientType.POLICE_DEPARTMENT,
              },
              {
                label: "Lead Agency",
                value: RecipientType.LEAD_AGENCY,
              },
              {
                label: "Custom",
                value: RecipientType.CUSTOM,
              },
            ]}
            sx={{
              "& .MuiFormControlLabel-root": {
                marginLeft: -1,
                marginBottom: 0.25,
              },
              "& .MuiRadio-root": {
                padding: "4px 8px",
              },
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
            }}
          />
        </Grid>
        <Grid item xs={8.5} md={3}>
          <RecipientPicker
            key={`recipientPicker-${rowId}`}
            recipientType={recipientType}
            facilityIds={facilityIds}
            control={control}
            disabled={disabled}
            index={index}
          />
        </Grid>

        {/* Middle Column - Communication Type */}
        <Grid item xs={3} md={1.75}>
          <Controller
            name={`recipients.${index}.communicationType`}
            control={control}
            render={({ field, fieldState }) => {
              const noContactAllowed = !isEmailAllowed && !isMailAllowed;
              const enableEmail = isEmailAllowed || noContactAllowed;
              const enableMail = isMailAllowed || noContactAllowed;
              const helperTextToShow =
                !isEmailAllowed && !isMailAllowed
                  ? "This recipient does not accept any form of communication, pick one at your discretion."
                  : fieldState.error?.message;

              return (
                <FormControl component="fieldset" size="small" fullWidth>
                  <FormLabel required>Mail Type</FormLabel>
                  <CompactRadioGroup
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      onChooseCommunicationType(
                        e.target.value as CommunicationType
                      );
                    }}
                  >
                    <Stack spacing={0.25}>
                      <CompactFormControlLabel
                        value={CommunicationType.CertifiedMail}
                        control={
                          <Radio
                            disabled={
                              disabled || !enableMail || preferUncertified
                            }
                            size="small"
                          />
                        }
                        label="Certified Mail"
                      />
                      <CompactFormControlLabel
                        value={CommunicationType.FirstClassMail}
                        control={
                          <Radio
                            disabled={disabled || !enableMail}
                            size="small"
                          />
                        }
                        label="Uncertified Mail"
                      />
                      <CompactFormControlLabel
                        value={CommunicationType.Email}
                        control={
                          <Radio
                            disabled={disabled || !enableEmail}
                            size="small"
                          />
                        }
                        label="Email"
                      />
                    </Stack>
                  </CompactRadioGroup>
                  {helperTextToShow && (
                    <Typography color="error" variant="caption">
                      {helperTextToShow}
                    </Typography>
                  )}
                </FormControl>
              );
            }}
          />
        </Grid>

        {/* Right Column - Contact Information */}
        <Grid item xs={8.5} md={5}>
          <Stack direction="row" gap={2}>
            <Grid
              item
              xs="auto"
              md="auto"
              sx={{
                mt: communicationType === CommunicationType.Email ? 2.5 : 2,
                mr: -1,
              }}
            >
              {communicationType === CommunicationType.Email ? (
                <AlternateEmailIcon sx={{ color: "#64B5F6" }} />
              ) : (
                <PlaceIcon sx={{ color: "#F06292" }} />
              )}
            </Grid>
            <Grid container spacing={2}>
              {communicationType === CommunicationType.Email ? (
                <>
                  <Grid item xs={12}>
                    <EmailArrayField
                      key={`emailRecipientAddresses-${rowId}`}
                      label="Email"
                      name={`recipients.${index}.emailRecipientAddresses`}
                      disabled={disabled}
                      hideErrors={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      key={`emailReplyTo-${rowId}`}
                      name={`recipients.${index}.emailReplyTo`}
                      disabled={disabled}
                      control={control}
                      label="Reply To"
                      hideErrors={true}
                      preserveEmptyString={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      key={`emailSubject-${rowId}`}
                      name={`recipients.${index}.emailSubject`}
                      disabled={disabled}
                      control={control}
                      label="Subject"
                      hideErrors={true}
                      preserveEmptyString={true}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <FormTextField
                      key={`mailRecipientAddressLine1-${rowId}`}
                      name={`recipients.${index}.mailRecipientAddressLine1`}
                      control={control}
                      label="Street Address"
                      disabled={disabled}
                      hideErrors={true}
                      preserveEmptyString={true}
                      textFieldProps={{ required: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormTextField
                      key={`mailRecipientAddressLine2-${rowId}`}
                      name={`recipients.${index}.mailRecipientAddressLine2`}
                      control={control}
                      label="Street Address 2"
                      disabled={disabled}
                      hideErrors={true}
                      preserveEmptyString={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormTextField
                      key={`mailRecipientCity-${rowId}`}
                      name={`recipients.${index}.mailRecipientCity`}
                      control={control}
                      label="City"
                      disabled={disabled}
                      hideErrors={true}
                      preserveEmptyString={true}
                      textFieldProps={{ required: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StatePicker
                      key={`mailRecipientState-${rowId}`}
                      name={`recipients.${index}.mailRecipientState`}
                      // @ts-ignore TODO: figure out why this type is not compatible
                      control={control}
                      label="State"
                      disabled={disabled}
                      hideErrors={true}
                      textFieldProps={{ required: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      key={`mailRecipientZip-${rowId}`}
                      name={`recipients.${index}.mailRecipientZip`}
                      control={control}
                      label="Zip Code"
                      disabled={disabled}
                      hideErrors={true}
                      preserveEmptyString={true}
                      textFieldProps={{ required: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Stack>
        </Grid>
        <Grid item xs={0.5} md={0.5}>
          {!disabled && (
            <IconButton onClick={onRemove} sx={{ ml: -1 }}>
              <RemoveIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
      {communicationType === CommunicationType.Email && (
        <Grid item xs={12} md={12}>
          <EmailBodyEditor
            key={`emailBody-${rowId}`}
            index={index}
            disabled={disabled}
          />
        </Grid>
      )}
    </Box>
  );
};
